import { MoreOutlined } from "@ant-design/icons"
import {
  Button,
  type ButtonProps,
  Dropdown,
  type DropdownProps,
  type MenuProps,
  theme
} from "antd"
import React from "react"
import { useTranslation } from "react-i18next"

import { cn } from "../../utils/cn.ts"
import { unavailableProps } from "../../utils/unavailable-control.ts"
import { ActionTooltip } from "../ActionTooltip.tsx"
import { rowActionsDropdownProps } from "./row-actions-popup-container.ts"

interface RowActionsMenuProps {
  items: MenuProps["items"]
  disabled?: boolean
  placement?: DropdownProps["placement"]
  /**
   * For a column whose items carry a key rather than a handler of their own.
   */
  onClick?: MenuProps["onClick"]
  /**
   * Overrides for the trigger, so a table with its own affordance can adopt
   * this without changing how the row looks. The container is not reachable
   * from here — that is the point.
   */
  buttonProps?: Omit<ButtonProps, "icon" | "onClick" | "disabled">
}

/**
 * The "⋯" menu at the end of a table row.
 *
 * Owning the container here rather than passing it at each call site is the
 * point: a table that scrolls its body internally leaves a menu parked on
 * `document.body` hanging over the wrong row, and that only has to be
 * remembered once instead of at every Actions column.
 */
export function RowActionsMenu({
  items,
  disabled = false,
  placement = "bottom",
  onClick,
  buttonProps
}: RowActionsMenuProps) {
  const { t } = useTranslation()
  const menuLabel = buttonProps?.["aria-label"] ?? t("table.rowActions.more")
  // The row underneath opens or edits the record on click.
  const unavailable = unavailableProps(disabled, (event) =>
    event.stopPropagation()
  )

  // The row height the menu actually lays its items out at, from the theme
  // rather than restated here.
  const {
    token: { controlHeightSM }
  } = theme.useToken()

  return (
    // Dropdown must own the trigger; both clone their child.
    <ActionTooltip title={menuLabel}>
      <Dropdown
        menu={{ items, onClick }}
        // `Dropdown disabled` would put a real `disabled` back on the button.
        trigger={disabled ? [] : ["click"]}
        placement={placement}
        {...rowActionsDropdownProps(items, controlHeightSM)}
      >
        <Button
          {...buttonProps}
          {...unavailable}
          aria-label={menuLabel}
          className={cn(buttonProps?.className, unavailable.className)}
          icon={<MoreOutlined className={"text-md"} />}
        />
      </Dropdown>
    </ActionTooltip>
  )
}

export default RowActionsMenu
